import { Box, type SxProps, type Theme } from "@mui/material";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_MEDIA,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { LinkCard } from "./LinkCard";
import { SiteImage } from "./media/SiteImage";
import type { SiteImageName } from "./media/siteMedia";
import { SectionHead } from "./SectionHead";
import { SiteSection } from "./SiteSection";

const EDUCATION_IMAGES: readonly SiteImageName[] = [
  "edu_ctrl",
  "plans_c",
  "clipboard",
  "gear",
];
/** 그리기 전 자리 높이(넓은 화면, 900px 이하) — 시안 `--cis`·`--cism` */
const EDUCATION_OFFSCREEN_SIZE = [1480, 1856] as const;

const EDUCATION_STYLES = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: "clamp(48px,6vw,88px) clamp(24px,3vw,40px)",
    [SITE_MEDIA.phone]: { gridTemplateColumns: "1fr", gap: "44px" },
  },
} as const;

interface EducationCardText {
  kicker: string;
  title: string;
  text: string;
  alt: string;
}

/** 교육·컨설팅·법규·안전 교육 네 카드(홈·드론 측량 공통). */
export function EducationCards({
  landing = false,
  sectionSx,
}: {
  landing?: boolean;
  sectionSx?: SxProps<Theme>;
}) {
  const { t } = useTranslation("landing");
  const titleId = useId();
  const cards = t("site.education.cards", {
    returnObjects: true,
  }) as readonly EducationCardText[];
  return (
    <SiteSection
      labelledBy={titleId}
      skipOffscreen={EDUCATION_OFFSCREEN_SIZE}
      sx={sectionSx}
    >
      <Box sx={SITE_WRAP}>
        <SectionHead
          titleId={titleId}
          eyebrow={t("site.education.eyebrow")}
          title={t("site.education.title")}
          landing={landing}
        />
        <Box sx={EDUCATION_STYLES.grid}>
          {cards.map((card, index) => (
            <LinkCard
              key={card.kicker}
              revealIndex={index}
              media={
                <SiteImage
                  name={EDUCATION_IMAGES[index]}
                  sizes="(max-width: 640px) 100vw, 600px"
                  alt={card.alt}
                />
              }
              kicker={card.kicker}
              title={card.title}
              text={card.text}
            />
          ))}
        </Box>
      </Box>
    </SiteSection>
  );
}
